<template>
  <ContentSection
    :class="!data.color_theme?.value || data.color_theme?.value === 'blue' ? `bg-theme-500 text-white` : `
      bg-theme-200
    `"
    :style="colorTheme"
  >
    <Container indent="default">
      <!-- TITLE -->
      <h2 v-animate-in-from-bottom class="mb-16 typo-label-md sm:mb-28 sm:typo-label-lg" v-html="data.heading" />
    </Container>

    <!-- FACTS -->
    <div v-animate-children-in-from-left class="grid gap-y-26 pb-6">
      <ul
        v-for="(chunk, chunkIndex) in factChunks"
        :key="chunkIndex"
        v-drag-scroll
        class="flex select-none pr-11 lg:pr-[12.5rem] md:pr-[5.25rem]"
        :class="chunk[0]?.width?.value === 'wide' ? 'gap-x-6 gap-y-18 sm:gap-x-14':`
          gap-x-6 gap-y-14
          sm:gap-x-10
        `"
      >
        <li
          v-for="(fact, index) in chunk"
          :key="fact?.id || index"
          class="flex"
          :class="fact?.width?.value === 'wide' ? 'flex-wide' : 'flex-narrow'"
        >
          <div
            class="
              size-full translate-x-container border-b-[0.09375rem] pb-5
              lg:translate-x-container-desktop
              md:translate-x-container-lg
            "
          >
            <!-- Icon -->
            <div v-if="fact?.icon" class="mb-10 h-18 sm:mb-14 sm:h-24">
              <AppImage
                :src="fact.icon"
                :alt="useMapAltText(fact.icon)"
                class="h-full object-contain object-left"
              />
            </div>

            <!-- Title -->
            <Counter
              class="mb-5 typo-heading-xl sm:mb-6 sm:typo-heading-3xl"
              :value="fact?.title"
              tag="h3"
              :add-spaces-to-number="fact?.separate_number_with_space"
            />

            <!-- Text -->
            <p class="pr-4 typo-body-xl sm:typo-body-2xl" v-html="fact?.text" />
          </div>
        </li>
      </ul>
    </div>
  </ContentSection>
</template>

<script lang="ts" setup>
import type { ComponentsMainFactsFragment } from '#gql'

const props = defineProps<{
  data: Enforce<ComponentsMainFactsFragment>
  prevComponent?: string | null
  nextComponent?: string | null
}>()

const colorTheme = useGetColorThemeStylesComponent(props.data.color_theme?.value)

const factChunks = computed(() => {
  const chunks: Array<Array<NonNullable<ComponentsMainFactsFragment['facts']>[number]>> = []
  let currentChunk: Array<NonNullable<ComponentsMainFactsFragment['facts']>[number]> = []
  let forceNewChunk = false

  props.data.facts?.forEach((fact, index) => {
    if (!fact) return // Skip null/undefined facts

    const isWide = fact.width?.value === 'wide'
    const isCurrentChunkWide = currentChunk[0]?.width?.value === 'wide'

    if (currentChunk.length === 0 || (isWide === isCurrentChunkWide && !forceNewChunk)) {
      currentChunk.push(fact)
    } else {
      chunks.push(currentChunk)
      currentChunk = [fact]
    }

    forceNewChunk = !!fact.line_break_after

    if (index === (props.data.facts?.length ?? 0) - 1 && currentChunk.length > 0) {
      chunks.push(currentChunk)
    }
  })

  return chunks
})
</script>

<style scoped>
.flex-wide {
  flex: 1 1 24rem;
  min-width: min(22rem, 70vw);
    max-width: 26.5rem;
}

.flex-narrow {
  flex: 1 1 17.5rem;
  min-width: min(15rem, 70vw);
  max-width: 19.6rem;
}
</style>
